import React, { useState, useEffect } from "react";
import { Search, Download, ShieldAlert, RefreshCw, CheckCircle, AlertTriangle, X } from "lucide-react";
import { getDriverPayoutPaymentSummary, updateDriverPayoutStatus, getAllDriverPayouts } from "../../api/DriverPayoutApi";
import { getAllClientClaims } from "../../api/ClientClaimApi";
import { crossTally, validateTrips } from "../../api/reconciliationApi";
import { getExportFileName } from "../../utils/fileExportNaming";
import CustomSelect from "../shared/CustomSelect";
import { toast } from "../shared/Toast";

const Payments = () => {
  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard', 'reconciliation', 'tripValidation'

  // Dashboard Payouts state
  const [payments, setPayments] = useState([]);
  const [allStats, setAllStats] = useState({ total: 0, paid: 0, pending: 0, hold: 0, vehicles: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all"); // 'all', 'APW', 'APO'
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'Pending', 'Paid', 'Hold'
  const [showPreview, setShowPreview] = useState(false);
  const [previewPayments, setPreviewPayments] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState({});
  const [uploadModalData, setUploadModalData] = useState(null);
  const [openStatusId, setOpenStatusId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });

  // Reconciliation state
  const [claims, setClaims] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState("");
  const [selectedPayoutId, setSelectedPayoutId] = useState("");
  const [tallyResult, setTallyResult] = useState(null);
  const [tallyLoading, setTallyLoading] = useState(false);

  // Trip Validation state
  const [validationResult, setValidationResult] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);

  // Load payment summary when page or filters change
  useEffect(() => {
    fetchPaymentSummary(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, categoryFilter, statusFilter]);

  // Initial stats fetch
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "reconciliation") {
      loadReconciliationData();
    } else if (activeTab === "tripValidation") {
      loadTripValidationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setCurrentPage(1);
  };

  const handleCategoryChange = (val) => {
    setCategoryFilter(val);
    setCurrentPage(1);
  };

  const handleStatusChangeFilter = (val) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.currentPage) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = (current, total) => {
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [];
    if (current <= 3) {
      pages.push(1, 2, 3, 4, "...", total);
    } else if (current >= total - 2) {
      pages.push(1, "...", total - 3, total - 2, total - 1, total);
    } else {
      pages.push(1, "...", current - 1, current, current + 1, "...", total);
    }
    return pages;
  };

  const fetchPaymentSummary = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
      };
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await getDriverPayoutPaymentSummary(params);
      if (res.success) {
        setPayments(res.data || []);
        setPagination({
          totalCount: res.totalCount !== undefined ? res.totalCount : (res.data?.length || 0),
          totalPages: res.totalPages || 1,
          currentPage: res.currentPage || page,
          limit: res.limit || pageSize,
        });
        if (res.stats) {
          setAllStats({
            total: res.stats.totalVehicles,
            paid: res.stats.paid,
            pending: res.stats.pending,
            hold: res.stats.hold,
            vehicles: res.stats.totalVehicles,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching payment summary", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getDriverPayoutPaymentSummary({ limit: 1 });
      if (res.success && res.stats) {
        setAllStats({
          total: res.stats.totalVehicles,
          paid: res.stats.paid,
          pending: res.stats.pending,
          hold: res.stats.hold,
          vehicles: res.stats.totalVehicles,
        });
      }
    } catch (error) {
      console.error("Error fetching stats", error);
    }
  };

  const loadReconciliationData = async () => {
    try {
      const [claimsRes, payoutsRes] = await Promise.all([
        getAllClientClaims(),
        getAllDriverPayouts({ limit: "all" })
      ]);
      if (claimsRes.success) {
        setClaims(claimsRes.data || claimsRes.claims || []);
      }
      if (payoutsRes.success) {
        setAllPayouts(payoutsRes.data || payoutsRes.payouts || []);
      }
    } catch (err) {
      console.error("Failed to load claims or payouts", err);
    }
  };

  const loadTripValidationData = async () => {
    try {
      setValidationLoading(true);
      const res = await validateTrips();
      if (res.success) {
        setValidationResult(res.data || []);
      }
    } catch (err) {
      console.error("Failed to run trip validation", err);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleCrossTally = async () => {
    if (!selectedClaimId || !selectedPayoutId) {
      toast.warning("Please select both a Client Claim and a Driver Payout record");
      return;
    }
    try {
      setTallyLoading(true);
      const res = await crossTally(selectedClaimId, selectedPayoutId);
      if (res.success) {
        setTallyResult(res.data);
      }
    } catch (err) {
      toast.error("Failed to run cross tally: " + err.message);
    } finally {
      setTallyLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await updateDriverPayoutStatus(id, newStatus);
      if (res.success) {
        // Refresh data from backend to reflect new status
        await Promise.all([fetchPaymentSummary(currentPage), fetchStats()]);
      }
    } catch (error) {
      console.error("Failed to update status", error);
      toast.error("Error updating status: " + error.message);
    }
  };

  // All filtering is handled by backend - payments are already filtered
  const filteredPayments = payments;

  // Calculate statistics from unfiltered totals
  const totalVehicles = allStats.vehicles;
  const paidCount = allStats.paid;
  const pendingCount = allStats.pending;
  const holdCount = allStats.hold;

  const pendingPayments = previewPayments;

  const handleOpenExportPreview = async () => {
    try {
      setLoading(true);
      const params = {
        status: "Pending",
        limit: "all",
      };
      if (categoryFilter && categoryFilter !== "all") {
        params.category = categoryFilter;
      }
      const res = await getDriverPayoutPaymentSummary(params);
      if (res.success) {
        const list = res.data || [];
        setPreviewPayments(list);
        const initialSelected = {};
        list.forEach((p) => {
          initialSelected[p.id] = true;
        });
        setSelectedPayouts(initialSelected);
        setShowPreview(true);
      }
    } catch (err) {
      toast.error("Failed to load payments for export: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionCode = (p, index) => {
    if (p?.remarks && typeof p.remarks === "string" && /^[A-Z]{3}\d{3};$/.test(p.remarks.trim())) {
      return p.remarks.trim();
    }

    const rawDate = p?.toDate || p?.fromDate || p?.createdAt;
    const dateObj = rawDate ? new Date(rawDate) : new Date();
    const validDate = isNaN(dateObj.getTime()) ? new Date() : dateObj;
    
    // 3-letter month abbreviation (e.g., JAN, FEB, SEP, AUG)
    const monthStr = validDate.toLocaleString("en-US", { month: "short" }).toUpperCase();

    // Unique 3-digit number (001 - 999)
    let num = 0;
    if (p?.id) {
      const str = String(p.id);
      for (let i = 0; i < str.length; i++) {
        num = (num * 31 + str.charCodeAt(i)) % 900;
      }
      num = (Math.abs(num + (index || 0)) % 900) + 1;
    } else {
      num = ((index || 0) % 900) + 1;
    }
    const threeDigits = String(num).padStart(3, "0");

    // Format: MONTH3digits; (e.g. JAN284;, AUG001;)
    return `${monthStr}${threeDigits};`;
  };

  const downloadCSV = () => {
    const targetPayments = pendingPayments.filter(p => selectedPayouts[p.id]);
    if (targetPayments.length === 0) {
      toast.warning("Please select at least one payment to download");
      return;
    }

    const csvRows = [];
    targetPayments.forEach((p, idx) => {
      const cat = p.driver?.vehicle?.accountDetails?.bankCategory || "APW";
      const amount = p.payableAmount || 0;
      const accNum = p.driver?.vehicle?.accountDetails?.accountNumber || "";
      const name = p.driver?.vehicle?.accountDetails?.accountHolderName || p.driver?.driverName || "";
      const remarks = getTransactionCode(p, idx);

      if (cat === "APW") {
        csvRows.push([cat, amount, accNum, name, "INR", remarks].join(","));
      } else {
        const ifsc = p.driver?.vehicle?.accountDetails?.ifscCode || "";
        csvRows.push([cat, amount, accNum, name, "INR", "NFT", ifsc, remarks].join(","));
      }
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const fileName = getExportFileName({
      reportName: `payment_bank_upload_${categoryFilter}`,
      data: targetPayments,
      ext: "csv",
    });
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowPreview(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split("\n").map(line => line.trim()).filter(Boolean);

      // Fetch all pending payments to match against bank upload
      let pendingList = [];
      try {
        const res = await getDriverPayoutPaymentSummary({ status: "Pending", limit: "all" });
        if (res.success) {
          pendingList = res.data || [];
        }
      } catch (err) {
        console.error("Error fetching pending payments for reconciliation:", err);
      }

      const paymentsToUpdate = [];
      let matchedCount = 0;


      lines.forEach((line, index) => {
        const parts = line.split(/[\t,]/).map(p => p.trim().replace(/^"|"$/g, ''));
        if (parts.length < 5) {
          return;
        }

        // Skip headers
        if (index === 0 && (parts[0].toLowerCase().includes("category") || isNaN(Number(parts[1])))) {
          return;
        }

        const amount = Number(parts[1]);
        const accountNumber = parts[2] || "";
        const name = parts[3] || "";

        // Check if any column in this row contains a success key (handles trailing commas or varying column positions)
        const isTrue = parts.some(p => ["true", "success", "paid", "yes", "done", "1"].includes(p.toLowerCase().trim()));


        if (isTrue) {
          // Find matching payment in pending payments list using smart fallbacks
          const match = pendingList.find(p => {
            const pAcc = String(p.driver?.vehicle?.accountDetails?.accountNumber || "").trim();
            const pAmt = Number(p.payableAmount || 0);
            const pVeh = String(p.driver?.vehicle?.vehicleNumber || p.driver?.vehicle?.registrationNumber || "").trim();
            const pDrv = String(p.driver?.driverName || "").trim();

            const isAmtMatch = Math.abs(pAmt - amount) < 1;
            if (!isAmtMatch) return false;


            // 1. Try matching on account number if valid
            if (accountNumber && !accountNumber.includes("E+") && pAcc) {
              if (pAcc === accountNumber) return true;
            }

            // 2. Fallback to matching on vehicle registration number
            if (pVeh) {
              const cleanVeh = pVeh.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
              const cleanCSVName = name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
              const cleanCSVAcc = accountNumber.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

              if (
                cleanCSVName.includes(cleanVeh) ||
                cleanVeh.includes(cleanCSVName) ||
                cleanCSVAcc.includes(cleanVeh) ||
                cleanVeh.includes(cleanCSVAcc)
              ) {
                return true;
              }
            }

            // 3. Fallback to matching on driver name
            if (pDrv) {
              const cleanDrv = pDrv.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
              const cleanCSVName = name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
              if (cleanCSVName.includes(cleanDrv)) {
                return true;
              }
            }

            return false;
          });

          if (match) {
            if (match.status === "Pending") {
              paymentsToUpdate.push(match);
              matchedCount++;
            }
          }
        }
      });

      if (paymentsToUpdate.length === 0) {
        toast.info("No pending payments with key 'true' were matched in the file.");
        return;
      }

      setUploadModalData({
        matchedCount,
        paymentsToUpdate
      });
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const confirmBulkUpdate = async () => {
    if (!uploadModalData) return;

    setLoading(true);
    const list = uploadModalData.paymentsToUpdate;
    setUploadModalData(null);

    let successCount = 0;
    try {
      for (const p of list) {
        const res = await updateDriverPayoutStatus(p.id, "Paid");
        if (res.success) {
          successCount++;
        }
      }
      toast.success(`Successfully marked ${successCount} payments as Paid.`);
    } catch (err) {
      console.error(err);
      toast.error("Error updating status for some payments.");
    } finally {
      await Promise.all([fetchPaymentSummary(currentPage), fetchStats()]);
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusColors = (status) => {
    switch (status) {
      case "Paid":
        return { color: "#16A34A", bg: "#F0FDF4" };
      case "Hold":
        return { color: "#EF4444", bg: "#FEE2E2" };
      case "Pending":
      default:
        return { color: "#D97706", bg: "#FFFBEB" };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '12px' }}>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', gap: '16px', paddingBottom: '4px' }}>
        <button
          onClick={() => setActiveTab("dashboard")}
          style={{
            padding: '8px 16px',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            background: 'none',
            borderBottom: activeTab === "dashboard" ? '3px solid #227B48' : '3px solid transparent',
            color: activeTab === "dashboard" ? '#227B48' : '#6B7280',
            cursor: 'pointer',
            paddingBottom: '12px'
          }}
        >
          Payout Summary
        </button>
        <button
          onClick={() => setActiveTab("reconciliation")}
          style={{
            padding: '8px 16px',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            background: 'none',
            borderBottom: activeTab === "reconciliation" ? '3px solid #227B48' : '3px solid transparent',
            color: activeTab === "reconciliation" ? '#227B48' : '#6B7280',
            cursor: 'pointer',
            paddingBottom: '12px'
          }}
        >
          Claims Reconciliation
        </button>
        <button
          onClick={() => setActiveTab("tripValidation")}
          style={{
            padding: '8px 16px',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            background: 'none',
            borderBottom: activeTab === "tripValidation" ? '3px solid #227B48' : '3px solid transparent',
            color: activeTab === "tripValidation" ? '#227B48' : '#6B7280',
            cursor: 'pointer',
            paddingBottom: '12px'
          }}
        >
          Trip Validation
        </button>
      </div>

      {/* Tab 1: Dashboard Tab */}
      {activeTab === "dashboard" && (
        <>
          {/* Statistics Cards */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1F2937' }}>Payments Dashboard</h2>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '16px', flexWrap: 'wrap', width: '100%' }}>
              <div
                onClick={() => handleStatusChangeFilter("all")}
                style={{
                  textAlign: 'center',
                  flex: 1,
                  minWidth: '120px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === "all" ? '#F3F4F6' : 'transparent',
                  border: statusFilter === "all" ? '1px solid #9CA3AF' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '42px', fontWeight: '800', color: '#227B48', lineHeight: '1' }}>{totalVehicles}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', marginTop: '8px' }}>Total Vehicles</div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#E5E7EB', height: '60px' }}></div>
              <div
                onClick={() => handleStatusChangeFilter("Paid")}
                style={{
                  textAlign: 'center',
                  flex: 1,
                  minWidth: '120px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === "Paid" ? '#F0FDF4' : 'transparent',
                  border: statusFilter === "Paid" ? '1px solid #16A34A' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '42px', fontWeight: '800', color: '#16A34A', lineHeight: '1' }}>{paidCount}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', marginTop: '8px' }}>Paid Payouts</div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#E5E7EB', height: '60px' }}></div>
              <div
                onClick={() => handleStatusChangeFilter("Pending")}
                style={{
                  textAlign: 'center',
                  flex: 1,
                  minWidth: '120px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === "Pending" ? '#FFFBEB' : 'transparent',
                  border: statusFilter === "Pending" ? '1px solid #D97706' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '42px', fontWeight: '800', color: '#D97706', lineHeight: '1' }}>{pendingCount}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', marginTop: '8px' }}>Pending Payouts</div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#E5E7EB', height: '60px' }}></div>
              <div
                onClick={() => handleStatusChangeFilter("Hold")}
                style={{
                  textAlign: 'center',
                  flex: 1,
                  minWidth: '120px',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === "Hold" ? '#FEF2F2' : 'transparent',
                  border: statusFilter === "Hold" ? '1px solid #EF4444' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '42px', fontWeight: '800', color: '#EF4444', lineHeight: '1' }}>{holdCount}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', marginTop: '8px' }}>On Hold</div>
              </div>
            </div>
          </div>

          {/* Toolbar & Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search vehicle or driver..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{
                    padding: '10px 16px 10px 40px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    width: '260px'
                  }}
                />
                <Search size={18} color="#9CA3AF" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
              </div>

              <CustomSelect
                options={[
                  { value: 'all', label: 'All Categories' },
                  { value: 'APW', label: 'APW' },
                  { value: 'APO', label: 'APO' },
                ]}
                value={categoryFilter}
                onChange={(val) => handleCategoryChange(val)}
                width="170px"
              />

              <CustomSelect
                options={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Paid', label: 'Paid' },
                  { value: 'Hold', label: 'Hold' },
                ]}
                value={statusFilter}
                onChange={(val) => handleStatusChangeFilter(val)}
                width="160px"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleOpenExportPreview}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#227B48',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s'
                }}
              >
                <Download size={18} /> Download Payment Summary CSV
              </button>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s'
                }}
              >
                <RefreshCw size={18} /> Re-upload Payment Status
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          {/* Payments Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #E5E7EB', position: 'relative', zIndex: 10 }}>
            <div style={{ display: 'flex', backgroundColor: '#F9FAFB', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #E5E7EB', gap: '16px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
              <div style={{ flex: '2 1 0%' }}>Driver Name</div>
              <div style={{ flex: '1.8 1 0%' }}>Vehicle No</div>
              <div style={{ flex: '1.8 1 0%' }}>Bank Details</div>
              <div style={{ flex: '2 1 0%', textAlign: 'center' }}>Date Range</div>
              <div style={{ flex: '0.8 1 0%', textAlign: 'center' }}>Trips</div>
              <div style={{ flex: '1.2 1 0%', textAlign: 'right' }}>Total Amount</div>
              <div style={{ flex: '1.2 1 0%', textAlign: 'right' }}>Deductions</div>
              <div style={{ flex: '1.2 1 0%', textAlign: 'right' }}>Payable</div>
              <div style={{ flex: '1.2 1 0%', textAlign: 'center' }}>Status</div>
            </div>

            <div>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading payment summaries...</div>
              ) : filteredPayments.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>No payment summaries found matching filters.</div>
              ) : (
                filteredPayments.map((p, idx) => {
                  const colors = getStatusColors(p.status);
                  const isLastRow = idx === filteredPayments.length - 1;
                  const rowKey = p.id || idx;
                  const isDropdownOpen = openStatusId === rowKey;
                  return (
                    <div
                      key={rowKey}
                      style={{
                        display: 'flex',
                        padding: '16px 24px',
                        borderBottom: isLastRow ? 'none' : '1px solid #F3F4F6',
                        borderBottomLeftRadius: isLastRow ? '12px' : 0,
                        borderBottomRightRadius: isLastRow ? '12px' : 0,
                        alignItems: 'center',
                        fontSize: '14px',
                        color: '#333',
                        gap: '16px',
                        transition: 'background-color 0.15s',
                        position: 'relative',
                        zIndex: isDropdownOpen ? 50 : 1
                      }}
                    >
                      <div style={{ flex: '2 1 0%', fontWeight: 500, color: '#1F2937', wordBreak: 'break-word' }}>{p.driver?.driverName || "N/A"}</div>
                      <div style={{ flex: '1.8 1 0%', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                        {p.driver?.vehicle?.vehicleNumber || p.driver?.vehicle?.registrationNumber || "N/A"}
                      </div>
                      <div style={{ flex: '1.8 1 0%', display: 'flex', flexDirection: 'column', gap: '4px', wordBreak: 'break-word' }}>
                        <span style={{ fontWeight: 500, color: '#374151' }}>
                          {p.driver?.vehicle?.accountDetails?.bankName || "N/A"}
                        </span>
                        <div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            backgroundColor: p.driver?.vehicle?.accountDetails?.bankCategory === 'APW' ? '#EBF5FF' : '#FFFBEB',
                            color: p.driver?.vehicle?.accountDetails?.bankCategory === 'APW' ? '#1E40AF' : '#D97706'
                          }}>
                            {p.driver?.vehicle?.accountDetails?.bankCategory || "APW"}
                          </span>
                        </div>
                      </div>
                      <div style={{ flex: '2 1 0%', textAlign: 'center', color: '#4B5563' }}>
                        {formatDate(p.fromDate)} to {formatDate(p.toDate)}
                      </div>
                      <div style={{ flex: '0.8 1 0%', textAlign: 'center', fontWeight: 600 }}>{p.totalTrips}</div>
                      <div style={{ flex: '1.2 1 0%', textAlign: 'right', fontWeight: 500 }}>₹{Number(p.totalAmount || 0).toLocaleString()}</div>
                      <div style={{ flex: '1.2 1 0%', textAlign: 'right', color: '#DC2626' }}>₹{Number(p.totalDeduction || 0).toLocaleString()}</div>
                      <div style={{ flex: '1.2 1 0%', textAlign: 'right', fontWeight: 600, color: '#16A34A' }}>₹{Number(p.payableAmount || 0).toLocaleString()}</div>
                      <div style={{ flex: '1.2 1 0%', display: 'flex', justifyContent: 'center' }}>
                        <CustomSelect
                          variant="pill"
                          size="sm"
                          width="110px"
                          options={["Pending", "Paid", "Hold"]}
                          value={p.status}
                          isOpen={isDropdownOpen}
                          onOpenChange={(open) => setOpenStatusId(open ? rowKey : null)}
                          onChange={(val) => {
                            handleStatusChange(p.id, val);
                            setOpenStatusId(null);
                          }}
                          dropUp={filteredPayments.length > 3 && idx >= filteredPayments.length - 2}
                          buttonStyle={{
                            backgroundColor: colors.bg,
                            color: colors.color,
                            border: 'none',
                            fontWeight: 600,
                            padding: '6px 12px',
                            fontSize: '12px',
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Pagination Bar */}
          {pagination.totalCount > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "16px",
                padding: "16px 20px",
                backgroundColor: "white",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ fontSize: "14px", color: "#6B7280" }}>
                Showing{" "}
                <span style={{ fontWeight: 600, color: "#1F2937" }}>
                  {(pagination.currentPage - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span style={{ fontWeight: 600, color: "#1F2937" }}>
                  {Math.min(
                    pagination.currentPage * pagination.limit,
                    pagination.totalCount
                  )}
                </span>{" "}
                of{" "}
                <span style={{ fontWeight: 600, color: "#1F2937" }}>
                  {pagination.totalCount}
                </span>{" "}
                entries
              </div>

              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #D1D5DB",
                    backgroundColor:
                      pagination.currentPage <= 1 ? "#F3F4F6" : "white",
                    color: pagination.currentPage <= 1 ? "#9CA3AF" : "#374151",
                    cursor:
                      pagination.currentPage <= 1 ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: 500,
                    transition: "all 0.15s",
                  }}
                >
                  Previous
                </button>

                {getPageNumbers(pagination.currentPage, pagination.totalPages).map(
                  (p, idx) => {
                    if (p === "...") {
                      return (
                        <span
                          key={`ellipsis-${idx}`}
                          style={{
                            padding: "8px 6px",
                            color: "#9CA3AF",
                            fontSize: "14px",
                            userSelect: "none",
                          }}
                        >
                          ...
                        </span>
                      );
                    }
                    const isActive = pagination.currentPage === p;
                    return (
                      <button
                        key={`page-${p}`}
                        onClick={() => handlePageChange(p)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: "6px",
                          border: isActive ? "1px solid #227B48" : "1px solid #D1D5DB",
                          backgroundColor: isActive ? "#227B48" : "white",
                          color: isActive ? "white" : "#374151",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: isActive ? 600 : 500,
                          transition: "all 0.15s",
                        }}
                      >
                        {p}
                      </button>
                    );
                  }
                )}

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #D1D5DB",
                    backgroundColor:
                      pagination.currentPage >= pagination.totalPages
                        ? "#F3F4F6"
                        : "white",
                    color:
                      pagination.currentPage >= pagination.totalPages
                        ? "#9CA3AF"
                        : "#374151",
                    cursor:
                      pagination.currentPage >= pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "14px",
                    fontWeight: 500,
                    transition: "all 0.15s",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab 2: Claims Reconciliation */}
      {activeTab === "reconciliation" && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #E5E7EB' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1F2937', marginBottom: '24px' }}>Reconcile Driver Payouts with Client Claims</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                Select Client Claim Sheet
              </label>
              <CustomSelect
                options={[
                  { value: '', label: '-- Choose Client Claim Record --' },
                  ...claims.map((claim) => ({
                    value: claim.id,
                    label: `${claim.client?.clientName || "Unknown Client"} | ${claim.fromDate} to ${claim.toDate} (${claim.totalTrips} Trips)`
                  }))
                ]}
                value={selectedClaimId}
                onChange={(val) => setSelectedClaimId(val)}
                placeholder="-- Choose Client Claim Record --"
                searchable
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                Select Driver Payout Record
              </label>
              <CustomSelect
                options={[
                  { value: '', label: '-- Choose Driver Payout Record --' },
                  ...allPayouts.map((payout) => ({
                    value: payout.id,
                    label: `${payout.driver?.driverName || "Driver"} (${payout.driver?.vehicle?.vehicleNumber || "N/A"}) | ${payout.fromDate} to ${payout.toDate} (${payout.totalTrips} Trips)`
                  }))
                ]}
                value={selectedPayoutId}
                onChange={(val) => setSelectedPayoutId(val)}
                placeholder="-- Choose Driver Payout Record --"
                searchable
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
            <button
              onClick={handleCrossTally}
              disabled={tallyLoading || !selectedClaimId || !selectedPayoutId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#227B48',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: (tallyLoading || !selectedClaimId || !selectedPayoutId) ? 'not-allowed' : 'pointer',
                opacity: (tallyLoading || !selectedClaimId || !selectedPayoutId) ? 0.6 : 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              {tallyLoading ? <RefreshCw size={18} className="animate-spin" /> : <RefreshCw size={18} />} Run Reconciliation Check
            </button>
          </div>

          {tallyResult && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', padding: '24px', backgroundColor: '#F9FAFB' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#374151', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {tallyResult.status === "Matched" ? <CheckCircle size={20} color="#16A34A" /> : <AlertTriangle size={20} color="#EF4444" />}
                Reconciliation Result: <span style={{ color: tallyResult.status === "Matched" ? "#16A34A" : "#EF4444" }}>{tallyResult.status}</span>
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Client Claim Sheet</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1F2937', marginTop: '8px' }}>{tallyResult.clientTrips} Trips</div>
                  <div style={{ fontSize: '14px', color: '#4B5563', marginTop: '4px' }}>₹{Number(tallyResult.clientAmount).toLocaleString()} Claimed</div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Driver Payout Record</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1F2937', marginTop: '8px' }}>{tallyResult.driverTrips} Trips</div>
                  <div style={{ fontSize: '14px', color: '#4B5563', marginTop: '4px' }}>₹{Number(tallyResult.driverAmount).toLocaleString()} Payable</div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Tally Discrepancy</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: tallyResult.tripMatched ? '#16A34A' : '#EF4444', marginTop: '8px' }}>
                    {tallyResult.tripMatched ? "Trips Match" : "Trips Mismatch"}
                  </div>
                  <div style={{ fontSize: '14px', color: tallyResult.difference === 0 ? '#4B5563' : '#EF4444', marginTop: '4px', fontWeight: 600 }}>
                    Diff: ₹{Number(tallyResult.difference).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Trip Validation */}
      {activeTab === "tripValidation" && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1F2937' }}>Trip Sheet Vehicle Validation (SST vs Client)</h2>
            <button
              onClick={loadTripValidationData}
              disabled={validationLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#227B48',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={16} /> Re-run Validation
            </button>
          </div>

          <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', backgroundColor: '#F9FAFB', padding: '16px 24px', fontSize: '13px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ flex: 1 }}>Trip No</div>
              <div style={{ flex: 1.5, textAlign: 'center' }}>Client Vehicle No</div>
              <div style={{ flex: 1.5, textAlign: 'center' }}>SST Vehicle No</div>
              <div style={{ flex: 1.5, textAlign: 'center' }}>Validation Status</div>
            </div>

            <div>
              {validationLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Analyzing trip sheets and crosschecking vehicles...</div>
              ) : validationResult.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>No validated trips found. Make sure client claim trip sheets and SST format sheets are uploaded.</div>
              ) : (
                validationResult.map((v, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      padding: '16px 24px',
                      borderBottom: idx !== validationResult.length - 1 ? '1px solid #F3F4F6' : 'none',
                      alignItems: 'center',
                      fontSize: '14px',
                      color: '#333'
                    }}
                  >
                    <div style={{ flex: 1, fontWeight: 600 }}>{v.tripNo}</div>
                    <div style={{ flex: 1.5, textAlign: 'center', fontFamily: 'monospace' }}>{v.clientVehicle || "N/A"}</div>
                    <div style={{ flex: 1.5, textAlign: 'center', fontFamily: 'monospace' }}>{v.sstVehicle || "N/A"}</div>
                    <div style={{ flex: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: v.status === "MATCH" ? "#EBF5FF" : "#FFF1F2",
                        color: v.status === "MATCH" ? "#1E40AF" : "#E11D48"
                      }}>
                        {v.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Download Preview Modal */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: '90%', maxWidth: '900px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: '80vh', overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', backgroundColor: '#227B48', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: 600 }}>Bank CSV File Export Preview</h3>
              <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #F59E0B', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldAlert size={20} color="#D97706" />
                <span style={{ fontSize: '13px', color: '#92400E', fontWeight: 600 }}>
                  Review and select the payments to export. Only Payouts with "Pending" status are listed here.
                </span>
              </div>
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>
                      <th style={{ padding: '12px', width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={pendingPayments.length > 0 && Object.values(selectedPayouts).filter(Boolean).length === pendingPayments.length}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const next = {};
                            pendingPayments.forEach(p => {
                              next[p.id] = checked;
                            });
                            setSelectedPayouts(next);
                          }}
                        />
                      </th>
                      <th style={{ padding: '12px' }}>Category</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '12px' }}>Account Number</th>
                      <th style={{ padding: '12px' }}>Beneficiary Name</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Currency</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Type</th>
                      <th style={{ padding: '12px' }}>IFSC Code</th>
                      <th style={{ padding: '12px' }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPayments.length === 0 ? (
                      <tr>
                        <td colSpan="9" style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
                          No pending payments matching current filters.
                        </td>
                      </tr>
                    ) : (
                      pendingPayments.map((p, idx) => {
                        const cat = p.driver?.vehicle?.accountDetails?.bankCategory || "APW";
                        return (
                          <tr key={p.id || idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={{ padding: '12px', width: '40px' }}>
                              <input
                                type="checkbox"
                                checked={!!selectedPayouts[p.id]}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSelectedPayouts(prev => ({ ...prev, [p.id]: checked }));
                                }}
                              />
                            </td>
                            <td style={{ padding: '12px', fontWeight: 600 }}>{cat}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#16A34A' }}>₹{Number(p.payableAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', fontFamily: 'monospace' }}>{p.driver?.vehicle?.accountDetails?.accountNumber || "N/A"}</td>
                            <td style={{ padding: '12px' }}>{p.driver?.vehicle?.accountDetails?.accountHolderName || p.driver?.driverName || "N/A"}</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>INR</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>{cat === "APO" ? "NFT" : ""}</td>
                            <td style={{ padding: '12px', fontFamily: 'monospace' }}>{cat === "APO" ? (p.driver?.vehicle?.accountDetails?.ifscCode || "N/A") : ""}</td>
                            <td style={{ padding: '12px', fontSize: '12px', color: '#1F2937', fontFamily: 'monospace', fontWeight: 600 }}>{getTransactionCode(p, idx)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: '16px 24px', backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={downloadCSV}
                disabled={pendingPayments.length === 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#227B48',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: pendingPayments.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: pendingPayments.length === 0 ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                Download CSV Bank File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-upload Confirmation Modal */}
      {uploadModalData && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: '90%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', backgroundColor: '#227B48', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: 600 }}>Confirm Bulk Payment Status Re-upload</h3>
              <button onClick={() => setUploadModalData(null)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CheckCircle size={32} color="#16A34A" />
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                  {uploadModalData.matchedCount} Match(es) Found
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>
                The uploaded file contains payment records marked with the confirmation key. Clicking confirm will update their status to <strong>Paid</strong> in the system.
              </p>

              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px' }}>
                {uploadModalData.paymentsToUpdate.map((p, index) => (
                  <div key={p.id || index} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderBottom: index !== uploadModalData.paymentsToUpdate.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <span style={{ fontWeight: 500 }}>{p.driver?.driverName}</span>
                    <span style={{ color: '#16A34A', fontWeight: 600 }}>₹{Number(p.payableAmount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '16px 24px', backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setUploadModalData(null)}
                style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkUpdate}
                style={{ padding: '10px 20px', backgroundColor: '#227B48', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
              >
                Confirm & Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
